import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  IHistoryKpi,
  useDeleteKpiMutation,
  useKpiCreatedSubscription,
  useKpiUpdatedSubscription,
  useKpiDeletedSubscription,
  useGetHistoryKpisQuery
} from '../../../../generated/graphql';
import { canUserCreate } from '../../../../utils/rightUtils';
import { renderDate } from '../../../../utils/styleUtils';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import { Box } from '@mui/material';
import InnerLoader from '../../../Common/InnerLoader';
import DenseTable from '../../../Common/Tables/DenseTable';
import KpiForm from './KpiForm';

interface IProps {
  stockId: string;
  currencyCode: string | undefined;
  companyName: string;
}

const KpiTab: React.FunctionComponent<IProps> = ({
  stockId,
  currencyCode,
  companyName
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [kpi, setKpi] = useState<IHistoryKpi | null>(null);
  const [openKpiForm, setOpenKpiForm] = useState(false);
  const [allData, setAllData] = useState<IHistoryKpi[]>([]);
  const [deleteKpi] = useDeleteKpiMutation();
  const canCreate = canUserCreate(state.user, Types.Stock);
  const createKpiSubscription = useKpiCreatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const updateKpiSubscription = useKpiUpdatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const deleteKpiSubscription = useKpiDeletedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const { data, loading } = useGetHistoryKpisQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId as string)
    }
  });

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IHistoryKpi[]);
    }
  }, [data]);

  useEffect(() => {
    if (createKpiSubscription.data?.result) {
      addKpi(createKpiSubscription.data?.result);
    }
    if (updateKpiSubscription.data?.result) {
      updateKpi(updateKpiSubscription.data?.result);
    }
    if (deleteKpiSubscription.data?.result) {
      removeKpi(deleteKpiSubscription.data.result);
    }
  }, [createKpiSubscription.data, updateKpiSubscription.data, deleteKpiSubscription.data]);

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 82
    },
    {
      title: 'Date Day',
      field: 'date',
      width: 93,
      type: 'date',
      render: (rowData: IHistoryKpi) => renderDate(rowData.date)
    },
    {
      title: 'Company',
      width: 392,
      render: () => companyName,
      sorting: false
    },
    {
      title: 'Currency',
      width: 98,
      render: () => currencyCode,
      sorting: false
    },
    {
      title: 'eps',
      field: 'eps',
      width: 62
    },
    {
      title: 'NAV',
      field: 'nav',
      width: 62
    },
    {
      title: 'nnnnav',
      field: 'nnnav',
      width: 86
    },
    {
      title: 'niy %',
      field: 'niy',
      width: 74
    },
    {
      title: 'vacancy %',
      field: 'vacancy',
      width: 106
    },
    {
      title: 'Cost Ratio (incl.)',
      field: 'cost_ratio_incl',
      width: 143
    },
    {
      title: 'Cost Ratio (excl.)',
      field: 'cost_ratio_excl',
      width: 146
    },
    {
      title: 'NRA',
      field: 'nra',
      width: 68
    },
    {
      title: 'NTA',
      field: 'nta',
      width: 66
    },
    {
      title: 'NDV',
      field: 'ndv',
      width: 68
    },
    {
      title: 'Average',
      field: 'average',
      width: 92
    },
    {
      title: 'Remove',
      sorting: false,
      width: 104
    }
  ] as IHeadCell[];

  const handleDetails = (rowData: IHistoryKpi) => {
    setKpi(rowData);
    setOpenKpiForm(true);
  };

  const handleRemove = (rowData: IHistoryKpi) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete kpi from ${renderDate(rowData.date)}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IHistoryKpi) => {
    dispatch({ type: Actions.HideConfirmation });
    deleteKpi({
      variables: {
        id: rowData.id
      },
      update() {
        removeKpi(rowData);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Kpi from ${renderDate(rowData.date)} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const handleClose = (saved: boolean, isNew?: boolean, kpi?: IHistoryKpi) => {
    if (saved && kpi) {
      if (isNew) {
        addKpi(kpi);
      } else {
        updateKpi(kpi);
      }
    }

    setKpi(null);
    setOpenKpiForm(false);
  };

  const addKpi = (kpi: IHistoryKpi) => setAllData([kpi, ...allData]);

  const updateKpi = (kpi: IHistoryKpi) =>
    setAllData(allData.map((k) => (k.id === kpi.id ? kpi : k)));

  const removeKpi = (kpi: IHistoryKpi) => setAllData(allData.filter((k) => k.id !== kpi.id));

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <DenseTable
            columns={columns}
            data={allData}
            handleDetails={handleDetails}
            handleRemove={handleRemove}
            createButtonProps={{
              id: 'create-kpi-button',
              onClick: () => setOpenKpiForm(true),
              text: 'Create',
              disabled: !canCreate
            }}
            maxHeight={1050}
            pageSize={15}
          />
          {openKpiForm && (
            <KpiForm
              stockId={stockId}
              companyName={companyName}
              handleClose={handleClose}
              kpiInput={kpi}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default KpiTab;
